import { createSelector, weakMapMemoize } from '@reduxjs/toolkit';

import { countTotalBet, getBetType, getMinimalBet } from '../../../utils/bets';
import { computeBalance } from '../../compositeSelectors/helpers/computeBalance';
import type { CoreRootState } from '../../CoreStore';
import { selectFreeChipBalance } from '../bonusPromotions/selectors';
import { ChipTypes } from '../chips/types';
import {
  selectCurrencyDecimals,
  selectDeductibleBalance,
  selectIsBetsOpen,
  selectIsLoadedInActiveRound,
  selectUserBalance,
  selectWinResult,
} from '../game/rawSelectors';

import type { AcceptedBet, BetType, DragBufferType, IBetLimits, ILimit } from './types';

export const selectBets = (state: CoreRootState): BetType | undefined =>
  state.bets.betStack?.[state.bets.betStack.length - 1];
export const selectLastBet = (state: CoreRootState): BetType | null => state.bets.lastBet;
export const selectBetsReady = (state: CoreRootState): boolean => Boolean(state.bets.betList);
export const selectBetList = (state: CoreRootState): number[] | null => state.bets.betList;
export const selectBetLimits = (state: CoreRootState): IBetLimits | null => state.bets.betLimits;
export const selectMinBet = (state: CoreRootState): number => getMinimalBet(state.bets.betLimits);
export const selectRawTotalBet = (state: CoreRootState): number => state.bets.rawTotalBet;
export const selectDisabledBetSpots = (state: CoreRootState): string[] => state.bets.disabledBetSpots;
export const selectTotalBet = (state: CoreRootState): number => state.bets.totalBet;
export const selectHasTotalBet = (state: CoreRootState): boolean => state.bets.totalBet > 0;
export const selectAcceptedBets = (state: CoreRootState): AcceptedBet[] => state.bets.acceptedBets;
export const selectDragBuffer = (state: CoreRootState): DragBufferType | null =>
  state.bets.dragBuffer?.preliminary ? null : state.bets.dragBuffer;
export const selectPreliminaryDragBuffer = (state: CoreRootState): DragBufferType | null => state.bets.dragBuffer;
export const selectIsDragging = (state: CoreRootState): boolean => Boolean(state.bets.dragBuffer);
export const selectUpdateTimestamp = (state: CoreRootState): number => state.bets.updateTimestamp;
export const selectTableLimits = (state: CoreRootState): ILimit | null => state.bets.tableLimits;
export const selectPlacedBetType = (state: CoreRootState): string => state.bets.placedBetType;
export const selectTotalBetForAutoplay = (state: CoreRootState): number => {
  const decimals = state.gameState.currencyInfo?.digitsNumber ?? 2;
  const previousBet = state.bets.lastBet;
  const previousBetAmount = previousBet ? countTotalBet(previousBet?.betSpots, decimals) : 0;

  const currentBetAmount = state.bets.totalBet;

  return currentBetAmount > 0 ? currentBetAmount : previousBetAmount;
};

export const selectCompositeBalance = createSelector(
  [
    selectUserBalance,
    selectCurrencyDecimals,
    selectUpdateTimestamp,
    selectIsBetsOpen,
    selectIsLoadedInActiveRound,
    selectTotalBet,
    selectWinResult,
    selectPlacedBetType,
  ],
  (balance, decimals, betTimestamp, isBetsOpenStatus, isLoadedInActiveRound, totalBet, winResult, placedBetType) => {
    if (!balance) return;

    const balanceTimestamp = balance.updateTimestamp;
    const winResultTimestamp = winResult?.updateTimestamp ?? 0;
    /**
     * isLoadedInActiveRound - the server is sending in balance with the deducted bet in case of restore, so no need to do it manually as we do it otherwise.
     * isBetsOpenStatus - extra check for game state that the bets are actually closed and balance response from the server already should have the correct balance in response.
     * winResult - if we get in state where we have a win result, but the balance request has completed sooner and already should have the correct balance in response.
     */
    if (
      isLoadedInActiveRound ||
      (!isBetsOpenStatus && betTimestamp < balanceTimestamp) ||
      (winResult && winResultTimestamp < balanceTimestamp)
    ) {
      return balance;
    }

    return computeBalance(
      balance,
      totalBet,
      decimals,
      placedBetType === ChipTypes.FreeChip,
      /**
       * If the win result is the freshest of actions that affect balance - add it to the new balance amount (without subtracting totalBet).
       */
      winResult && winResultTimestamp > balanceTimestamp ? winResult.amount : undefined
    );
  },
  { argsMemoize: weakMapMemoize, memoize: weakMapMemoize }
);

export const selectMultiplierFactor = (state: CoreRootState): number | null => state.bets.multiplierFactor;
export const selectIsLowCompositeBalance = createSelector(
  selectCompositeBalance,
  selectFreeChipBalance,
  selectMinBet,
  (balance, freeChipBalance, minBet) => {
    const totalBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
    return freeChipBalance < minBet && totalBalance < minBet;
  }
);

export const selectReBetEnabled = createSelector(
  [
    selectDeductibleBalance,
    selectCurrencyDecimals,
    selectLastBet,
    selectMultiplierFactor,
    selectDisabledBetSpots,
    selectFreeChipBalance,
  ],
  (balance, digits, lastBet, multiplierFactor, disabledBetSpots, freeChipBalance) => {
    // Conditions
    // 1) there is a last bet
    // 2) It is not 0
    // 3) It does not include bet spots that are disabled now
    // 4) It fits in balance
    if (lastBet) {
      const savedBet = lastBet.betSpots;
      const betUnavailable = Object.keys(savedBet || {}).some((el) => {
        const value = savedBet?.[el]?.value ?? 0;
        return value > 0 && disabledBetSpots.includes(el);
      });
      let totalBet = countTotalBet(savedBet, digits);
      if (!betUnavailable) {
        const lastBetType = getBetType(lastBet);
        if (lastBetType === ChipTypes.FreeChip) {
          balance = freeChipBalance;
        } else if (multiplierFactor) {
          totalBet *= multiplierFactor;
        }
        return totalBet <= balance;
      }
    }
    return false;
  }
);

export const selectIsLowBalance = createSelector(
  [selectDeductibleBalance, selectFreeChipBalance, selectMinBet],
  (balance, freeChipBalance, minBet) => {
    return freeChipBalance < minBet && balance < minBet;
  }
);

export const selectInvalidBetspots = createSelector(
  [
    (state: CoreRootState): BetType | undefined => state.bets.betStack?.[state.bets.betStack.length - 1],
    (state: CoreRootState): IBetLimits | null => state.bets.betLimits,
  ],
  (placedBets, betLimits) => {
    if (placedBets && betLimits) {
      const { betSpots } = placedBets;
      return Object.keys(betSpots).filter((key) => {
        const betSpot = betSpots[key];
        const betLimit = betLimits[key];
        return betLimit && betSpot && betSpot.value > 0 && betSpot.value < betLimit.min;
      });
    }
    return [];
  }
);
